import type { ReactNode } from 'react';
import { PanelTitle, PageDescription } from '../layout/Typography';
import { cn } from '../../lib/utils';

interface ActionRowProps {
  /**
   * Optional leading element rendered before the title — a color swatch,
   * a small avatar, a status icon. Sits in a `shrink-0` slot so the title
   * still truncates.
   */
  leading?: ReactNode;
  /**
   * Row heading. Rendered through `PanelTitle` so the typography matches
   * other rows. Pass a node (e.g. `<code>…</code>`) when the visual needs
   * to break out of the default heading face.
   */
  title: ReactNode;
  /** Heading level. Defaults to `'h3'` since rows usually nest inside a section. */
  as?: 'h2' | 'h3';
  /**
   * Optional inline node placed to the right of the title text — a status
   * pill, a `MonoBadge`, etc. Wraps in a flex row so wide adornments line
   * up cleanly.
   */
  titleAdornment?: ReactNode;
  /** Supporting copy rendered below the title. */
  description?: ReactNode;
  /**
   * Optional secondary row beneath the title for meta chips (mono ids,
   * source labels, timestamps). Rendered as a `flex items-center gap-2`
   * row so the consumer just passes badges/spans.
   */
  meta?: ReactNode;
  /**
   * Right-aligned action slot — typically a tight group of `Button`s with
   * `size="sm"`, a single `Badge`, or nothing. The component wraps it in
   * a `shrink-0` flex group so multi-button setups align consistently.
   */
  actions?: ReactNode;
  /**
   * Padding density.
   * - `'comfortable'` (default) — `px-4 py-3`, matches the
   *   `divide-y rounded-lg border` list shape used on admin pages.
   * - `'compact'` — `px-2.5 py-2`, for tighter inline lists.
   */
  density?: 'comfortable' | 'compact';
  className?: string;
  /** Lands on the root row; lets stories scope by row id. */
  'data-testid'?: string;
}

/**
 * List-item row primitive: identifier on the left, action(s) on the right.
 *
 * Pairs with `SettingsRow` to cover the two row shapes that show up across
 * the admin and org surfaces:
 *
 * - **`SettingsRow`** — title + description, with a single primary control
 *   (Switch, Select, Button). Used for settings forms.
 * - **`ActionRow`** — title + description + optional meta + 0–N actions
 *   (icon buttons, badges). Used for list rows like "Imported Themes",
 *   "Required Secrets", "Service Accounts".
 *
 * Both render the same title/description structure, so two adjacent
 * sections — one of settings, one of list items — share heading sizes,
 * description weight, and right-rail alignment.
 */
export function ActionRow({
  leading,
  title,
  as = 'h3',
  titleAdornment,
  description,
  meta,
  actions,
  density = 'comfortable',
  className,
  'data-testid': dataTestId,
}: ActionRowProps) {
  const padding = density === 'compact' ? 'px-2.5 py-2' : 'px-4 py-3';
  return (
    <div
      data-testid={dataTestId}
      className={cn('flex items-center justify-between gap-3', padding, className)}
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          {leading && <span className="shrink-0">{leading}</span>}
          <PanelTitle as={as} size="sm" className="truncate">
            {title}
          </PanelTitle>
          {titleAdornment}
        </div>
        {description && <PageDescription className="mt-0.5 text-xs">{description}</PageDescription>}
        {meta && <div className="mt-0.5 flex items-center gap-2">{meta}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
